import { t_element_lookup_list, t_write_guard, t_merge_strategy, t_access_mode, t_write_mode, t_update_mode, t_key, IIndexable, IKeyValueSynchronous, IKeyValueHooks, IDatasourceOptions, IMediated } from 'core-ts-types';
import { safeCompact, getElement, DB } from '../core/db_utilities';
import { IMetasource, Metasource } from './metasource';
import { ORM } from './../engine/orm';
import { IStateMemento } from './../core/state_memento';
import * as _4 from 'lodash';

export interface IDatasourceConstructor<T extends IIndexable> {
  new<E extends T, M extends Object = {}, N extends IIndexable = { id: t_key }>(options: Partial<IDatasourceOptions>): Datasource<T, M, N>;
}

export type IDatasourceState<T, M> = { db: DB<T>, model: M };

export interface IDatasource<T extends IIndexable, M extends Object = {}, N extends IIndexable = { id: t_key }> extends IStateMemento<IDatasourceState<T, M>>, IKeyValueSynchronous<T>, IKeyValueHooks<T> {
  configure(options: Partial<IDatasourceOptions>): this;
  intersection(lookup: t_element_lookup_list<T>): t_key[];
  contains(lookup: t_element_lookup_list<T>): boolean;

  difference<U extends IIndexable>(intersectionSet: t_element_lookup_list<T>, iterator?: (t: T) => U): U[];
  query(qry: (t: T) => boolean): T[];

  remap<U extends IIndexable>(fn: (u: U) => T): this;
  rewrite(fn: (db: DB<T>) => DB<T>): this;

  replace(db: DB<T> | T[]): DB<T>;

  each(fn: (t: T) => void): this;
  map(fn: (t: T) => T): this;

  size(): number;
  pack(): this;

  accessMode: t_access_mode;
  /** @deprecated use mergeStrategy */
  updateMode: t_update_mode | t_merge_strategy;
  /** @deprecated use writeGuard */
  writeMode: t_write_mode | t_write_guard;
  mergeStrategy: t_merge_strategy;
  writeGuard: t_write_guard;

  includeModel(model: M): this;
  readonly model: M;

  includeMetasource<R extends N>(inclusion: (datasource: this) => IMetasource<T, R>): this;
  createMetasource(): IMetasource<T, N>;
  metasource(): IMetasource<T, N>;

  readonly __metasource__: IMetasource<T, N>;
}

export class Datasource<T extends IIndexable, M = {}, N extends IIndexable = { id: t_key }> implements IDatasource<T, M, N> {

  private static DEFAULT_DATASOURCE_OPTIONS: IDatasourceOptions = {
    builderType: undefined,
    autoId: true,
    accessMode: 'read-write',
    mergeStrategy: 'assign',
    updateMode: 'assign',
    writeGuard: 'overwrite',
    writeMode: 'clobber'
  };

  protected _options: IDatasourceOptions;
  protected _db: DB<T>;
  private __model: M;

  __metasource: IMetasource<T, N>;

  constructor(options: Partial<IDatasourceOptions> = Datasource.DEFAULT_DATASOURCE_OPTIONS) {
    this.configure(options);
    this.clear();
  }

  configure(options: Partial<IDatasourceOptions>): this {
    this._options = _4.merge({}, Datasource.DEFAULT_DATASOURCE_OPTIONS, this._options, options);

    this.accessMode = this._options.accessMode;
    this.mergeStrategy = this._options.mergeStrategy || this._options.updateMode;
    this.writeGuard = this._options.writeGuard || this._options.writeMode;
    return this;
  }

  /**
   * add hooks to hijack operations on a Datasource to synchronize Usersource and Model objects with the Datasource
   * @param hooks a set of functions starting with `$pre` and `$post`
   */
  addHooks(hooks: Record<string, Function>): this {
    _4.each(hooks, (hook, methodName: string) => {
      this[methodName] = hook;
    });
    return this;
  }

  /**
   * add a Metasource object to this Datasource
   * @param inclusion a function allowing you to create your own Metasource, usually unnecessary
   */
  includeMetasource<R extends N>(inclusion: (datasource: this) => IMetasource<T, R>): this {
    this.__metasource = inclusion(this);
    return this;
  }

  /**
   * add a Metasource object to this Datasource
   * @param inclusion a function allowing you to create your own Metasource, usually unnecessary
   */
  createMetasource(): IMetasource<T, N> {
    this.__metasource = new Metasource<T, N>(this);
    return this.__metasource;
  }

  /**
   * @deprecated use `metasource()`
   */
  get __metasource__(): IMetasource<T, N> {
    return this.__metasource;
  }

  metasource(): IMetasource<T, N> {
    return this.__metasource;
  }

  set writeMode(writeMode: t_write_mode | t_write_guard) {
    this._options.writeGuard = ((this.__isWriteGuard(writeMode)) ? writeMode : 'overwrite');
  }

  private __isWriteGuard(writeMode: string): writeMode is t_write_guard {
    return writeMode !== 'clobber';
  }

  set accessMode(accessMode: t_access_mode) {
    this._options.accessMode = accessMode;
  }

  set updateMode(updateMode: t_update_mode | t_merge_strategy) {
    this._options.mergeStrategy = updateMode;
  }

  set mergeStrategy(mergeStrategy: t_merge_strategy) {
    this._options.mergeStrategy = mergeStrategy;
  }

  set writeGuard(writeGuard: t_write_guard) {
    this._options.writeGuard = writeGuard;
  }

  get writeMode(): t_write_mode | t_write_guard {
    return this._options.writeGuard;
  }

  get accessMode(): t_access_mode {
    return this._options.accessMode;
  }

  get updateMode(): t_update_mode | t_merge_strategy {
    return this._options.mergeStrategy;
  }

  get mergeStrategy(): t_merge_strategy {
    return this._options.mergeStrategy;
  }

  get writeGuard(): t_write_guard {
    return this._options.writeGuard;
  }

  /**
   * Append a `model` to the Datasource
   */
  includeModel(model: M): this {
    this.__model = _4.merge({}, this.__model, model);
    return this;
  }

  get model(): M {
    return this.__model;
  }

  /**
   * remove all empty elements
   */
  pack(): this {
    this._db = (_4 as any).pickBy(this._db, (o: T) => o !== undefined);
    return this;
  }

  /**
   * @return the number of elements
   */
  size(): number {
    return _4.size(this._db);
  }

  /**
   * all elements in datasource that are in the intersectionSet
   * @param intersectionSet IIndexable's or id's to match against Datasource entities
   * @param iterator a mapping function
   */
  cursor<U extends IIndexable>(intersectionSet: t_element_lookup_list<T>, iterator?: (t: T) => U): U[] {
    return _4.map(_4.compact(_4.map(intersectionSet, (o: t_key | T) => getElement(this._db, o)) as any) as T[], iterator);
  }

  /**
   * all elements in datasource that are not in the intersectionSet
   * @param intersectionSet IIndexable's or id's to match against Datasource entities
   * @param iterator a mapping function
   */
  difference<U extends IIndexable>(intersectionSet: t_element_lookup_list<T>, iterator?: (t: T) => U): U[] {
    return _4.map(_4.compact(_4.filter<any, any>(intersectionSet, (o: t_key | T) => {
      return !getElement(this._db, o) as any;
    })) as T[], iterator);
  }

  /**
   *
   * @param qry
   * @param iterator
   */
  query<U extends IIndexable>(qry: (t: T) => boolean = (_0) => true, iterator?: (t: T) => U): U[] {
    return _4.map(_4.filter(this._db, qry), iterator);
  }

  /**
   * useful for mapping to a new structure for the view or logging
   * @param fn map the entire cache to a new form
   */
  report<U>(fn: (db: DB<T>) => U): U {
    return fn(this._db);
  }

  /**
   * the object returned will replace the existing store within the datasource. Use this to remove multiple and add multiple elements
   * @param fn output a new set of elements from the existing set
   */
  rewrite(fn: (db: DB<T>) => DB<T>): this {
    const newSet = fn(this.read());

    this
      .clear()
      .write(newSet);
    return this;
  }

  /**
   * map a partial element to final form for each element in this datasource
   * @param fn update each element
   */
  remap<U extends IIndexable>(fn: (u: U) => T): this {
    _4.each(this._db, (u: U, k: string, db: DB<T>) => {
      const remapped = fn(u);
      db[remapped.id] = remapped;
    });
    return this;
  }

  /**
   * respond to every element or change the properties of elements
   */
  each(fn: (v: T) => void): this {
    _4.each(this._db, fn);
    return this;
  }

  /**
   * true if all elements exist in the datasource
   * @param lookup a set of id or value objects
   */
  contains(lookup: t_element_lookup_list<T>): boolean {
    return _4.every(lookup, (v: T) => this.__exists(this.__key(v)));
  }

  /**
   *
   * @param k
   */
  exists(k: IIndexable | t_key): boolean {
    return this.__exists(k);
  }
  private __exists(k: IIndexable | t_key): boolean {
    const lookup = (k.hasOwnProperty('id')) ? k['id'] : k;
    return typeof this._db[lookup] !== 'undefined';
  }

  private __key(t: T | number | string): string {
    return ((t.hasOwnProperty('id')) ? t['id'] : t) as string;
  }

  /**
   * clear and refill with
   */
  replace(db: DB<T> | T[]): DB<T> {
    return this.clear().write(db);
  }

  /**
   * Write elements and convert with respect to access/update/write modes
   */
  write(db: DB<T> | T[]): DB<T> {
    return this.__exec('Write', db);
  }
  private __write(db: DB<T> | T[]): DB<T> {

    const _writeRecord: DB<T> = {};

    if (this.accessMode === 'read') {
      return _writeRecord;
    }

    _4.each(db, (e: T | Object): void => {
      const o: T = this.put(e as T);
      _writeRecord[o.id] = o;
    });

    return _writeRecord;
  }

  /**
   * insert an element with respect to access/update/write modes
   * @param t
   */
  put(t: T): T {
    return this.__exec('Put', t);
  }
  private __put(t: T): T {

    if (this.accessMode === 'read') {
      return t;
    }

    const tId = parseInt(t.id as string, 10);

    if (!!this._options.autoId && (_4.isNaN(tId) || !_4.isNumber(tId))) {
      let _id: string;

      do {
        _id = _4.uniqueId();
      } while (!!this._db[_id]);

      t.id = parseInt(_id, 10);
    }

    const existing = this._db[t.id];

    if (this._options.mergeStrategy === 'merge' && !!existing) {
      return this._db[t.id] = this.__merge(existing, t);
    }

    const _o: T = this.__build(t);

    if (this.writeGuard === 'preserve' && this._db.hasOwnProperty(_o.id as string)) {
      return _o;
    }

    this._db[_o.id] = _o;
    ORM.mark(this as IMediated);
    return _o;
  }
  private __build(t: T | Object): T {
    const options: IDatasourceOptions = this._options;
    const BuilderClass: any = options.builderType;

    return (options.builderType && !(t instanceof BuilderClass))
      ? options.builderType.build(t)
      : t;
  }
  private __merge(cached: T, newType: T): T {
    const options: IDatasourceOptions = this._options;
    const BuilderClass: any = options.builderType;

    return (options.builderType && typeof options.builderType.merge === 'function')
      ? options.builderType.merge(cached, newType)
      : _4.merge(cached, newType);
  }

  /**
   *
   * @param keyOrT
   */
  del(keyOrT: T | t_key): string {
    return this.__exec('Del', keyOrT);
  }
  private __del(kOrV: T | t_key): string {
    const id: string = (typeof kOrV === 'string' || typeof kOrV === 'number')
      ? kOrV as string
      : (kOrV.id + '') as string;

    delete this._db[id];
    ORM.mark(this as IMediated);
    return id;
  }

  /**
   *
   */
  clear(): this {
    return this.__exec('Clear', undefined);
  }
  private __clear(): this {
    this._db = {};
    ORM.mark(this as IMediated);
    return this;
  }

  /**
   *
   */
  read(): DB<T> {
    return this.__exec('Read', undefined);
  }
  private __read(): DB<T> {
    return this._db;
  }

  import(state: IDatasourceState<T, M>): this {
    this.__model = state.model;
    this.write(state.db);
    return this;
  }

  export(): IDatasourceState<T, M> {
    return {
      model: this.__model,
      db: this._db
    };
  }

  /**
   * return all elements as an array
   */
  values(): T[] {
    return this.__exec('Values');
  }
  private __values(): T[] {
    return safeCompact(_4.compact(_4.toArray(this._db)));
  }

  /**
   *
   * @param k
   */
  get(k: t_key): T | undefined {
    return this.__exec('Get', k);
  }
  private __get(k: t_key): T | undefined {
    return this._db[k];
  }

  private __exec(cmd: string, ...param: any[]): any {

    const cmdOp: string = cmd.toLowerCase();
    const $pre = `$pre${cmd}`;
    const $post = `$post${cmd}`;

    if (typeof this[$pre] === 'function') {
      this[$pre](...param);
    }

    const rtn = this[`__${cmdOp}`](...param);

    if (typeof this[$post] === 'function') {
      this[$post](...param);
    }

    return rtn;
  }

  /**
   * @deprecated use #values
   */
  getAll(): (T | undefined)[] {
    console.error(`DEPRECATED: Datasource#getAll`);
    return _4.map(this._db, (_0: never, id: string) => this.get(id));
  }

  /**
   * amend each object.
   * the id field is constant.
   * @param fn update each element
   * @deprecated
   */
  map(fn: (t: T) => T): this {
    console.error(`DEPRECATED: Datasource#map`);
    return this.remap<T>(fn);
  }

  /**
   * @deprecated use `rewrite` so that what you return replaces the Datasource or `remap` so time is not wasted with `put`
   * @param fn
   */
  refresh(fn: (db: DB<T>) => DB<T>): DB<T> {
    console.error(`DEPRECATED: Datasource#refresh`);
    this.rewrite(fn);
    return this._db;
  }

  /**
   * @deprecated
   */
  intersection(lookup: t_element_lookup_list<T>): t_key[] {
    console.error(`DEPRECATED: Datasource#intersection`);
    return this.__cursor(lookup, (cache, id) => (!!cache[id]) ? id : undefined) as t_key[];
  }

  /**
   * @deprecated
   */
  private __cursor(set: t_element_lookup_list<T>, fn: (cache: DB<T>, id: t_key) => T | t_key | undefined): (T | t_key)[] {
    return _4.compact(_4.map(set, (o: any) => fn(this._db, this.__key(o)))) as (T | t_key)[];
  }
}



// WEBPACK FOOTER //
// ./app/vendor/utility-ts-orm/dist/types/datasource.ts